import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { supplierService, SupplierDTO } from '../../services/supplierService';
import { parseApiError } from '../../utils/errorUtils';
import '../../pages/Products/ProductCategoryDeletePage.css';
import { ArrowLeft, Trash2 } from 'lucide-react';

const SupplierDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [supplier, setSupplier] = useState<SupplierDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadSupplier();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadSupplier = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await supplierService.getSupplierById(id || '0');
      if (response?.data?.data) {
        setSupplier(response.data.data);
      }
      setLoading(false);
    } catch {
      setError(t('supplier.failedToLoadSupplier'));
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!supplier) return;
    setIsDeleting(true);
    try {
      await supplierService.deleteSupplier(supplier.id);
      navigate('/suppliers', { state: { message: t('supplier.supplierDeletedSuccess') } });
    } catch (err: unknown) {
      setError(parseApiError(err, t('supplier.failedToDeleteSupplier')).message);
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!supplier) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{error || t('supplier.supplierNotFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delete-container">
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={() => navigate(`/suppliers/${id}`)}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('supplier.backToSupplier')}
          </button>
        </nav>

        <div className="delete-content">
          <div className="delete-card delete-card-main">
            <div className="delete-card-header delete-card-header-warning">
              <Trash2 size={24} strokeWidth={2} className="delete-icon" />
              <h2 className="delete-card-title">{t('supplier.deleteConfirmationTitle')}</h2>
            </div>

            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                {error}
                <button type="button" className="btn-close" onClick={() => setError(null)} />
              </div>
            )}

            <div className="delete-card-body">
              <div className="delete-warning-section">
                <p className="delete-warning-text">
                  {t('supplier.deleteConfirmationMessage')}
                </p>
              </div>

              <div className="delete-category-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('supplier.supplierCode')}</label>
                  <p className="delete-info-value delete-code">{supplier.code}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('supplier.supplierName')}</label>
                  <p className="delete-info-value">{supplier.name}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('supplier.contactEmail')}</label>
                  <p className="delete-info-value">{supplier.contactEmail}</p>
                </div>
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('supplier.status')}</label>
                  <p className="delete-info-value">
                    <span className={`badge ${supplier.isActive ? 'badge-active' : 'badge-inactive'}`}>
                      {supplier.isActive ? t('supplier.active') : t('supplier.inactive')}
                    </span>
                  </p>
                </div>
              </div>

              <div className="delete-actions">
                <button
                  className="btn-cancel"
                  onClick={() => navigate(`/suppliers/${id}`)}
                  disabled={isDeleting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('common.deleting')}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={2} />
                      {t('supplier.deleteItem')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="delete-sidebar">
            <div className="delete-card">
              <div className="delete-card-header">
                <h3 className="delete-card-title">{t('supplier.safetyInformation')}</h3>
              </div>
              <div className="delete-card-body">
                <div className="delete-help-section">
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('supplier.permanentAction')}</strong>
                    <p>{t('supplier.deleteWarning')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('supplier.relatedData')}</strong>
                    <p>{t('supplier.deleteRelatedDataWarning')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('supplier.confirmationRequired')}</strong>
                    <p>{t('supplier.deleteConfirmationRequired')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SupplierDeletePage;
